// Floor Plans Section — Data extraction utilities

export interface FloorPlanImage {
  base_path: string;
  path: string;
  type: "floor_plan";
  featured?: boolean;
}

export interface FloorPlanRoomType {
  id: string;
  name: string;
  images: FloorPlanImage[];
  area: string | null;
  floor: string | null;
  facing: string | null;
  unitType: string;
  icon: string;
}

export interface SitePlanFloor {
  floor: string;
  image: string;
}

export interface SitePlanBuilding {
  building: string;
  number_of_floors: number;
  floor_details: SitePlanFloor[];
}

export interface FloorPlanData {
  roomTypes: FloorPlanRoomType[];
  sitePlan: SitePlanBuilding[];
  hasFloorPlans: boolean;
  hasSitePlan: boolean;
}

export interface ZoomState {
  level: number;
  scrollPosition: { x: number; y: number };
}

/**
 * Checks if the property data contains any floor plan images or site plan entries.
 * Only considers floor plans associated with room types (children_short), not property-level images.
 */
export const hasFloorPlanData = (data: any): boolean => {
  // Check room type (children_short) level images only
  const hasChildFloorPlanImages = data?.children_short?.some((child: any) =>
    child.images?.some((img: any) => img.type === "floor_plan"),
  );
  const hasSitePlan = data?.site_plan?.length > 0;
  return hasChildFloorPlanImages || hasSitePlan;
};

/**
 * Filters room types that have floor plan images and maps their metadata.
 */
export const extractFloorPlanRoomTypes = (childrenShort: any[]): FloorPlanRoomType[] => {
  if (!childrenShort || !Array.isArray(childrenShort)) return [];

  return childrenShort
    .filter((child: any) => child.images?.some((img: any) => img.type === "floor_plan"))
    .map((child: any) => {
      const floorPlanImages: FloorPlanImage[] = (child.images || [])
        .filter((img: any) => img.type === "floor_plan")
        .map((img: any) => ({
          base_path: img.base_path,
          path: img.path,
          type: "floor_plan" as const,
          ...(img.featured !== undefined && { featured: img.featured }),
        }));

      const meta = child.meta || {};
      const area =
        meta.area && meta.area_unit ? `${meta.area} ${meta.area_unit}` : meta.area || null;

      return {
        id: String(child.id),
        name: child.name || "",
        images: floorPlanImages,
        area,
        floor: meta.floor || null,
        facing: meta.facing || null,
        unitType: meta.unit_type || "",
        icon: child.icon || "",
      };
    });
};

/**
 * Maps and sorts site plan buildings, sorting floor_details by floor number.
 */
export const extractSitePlan = (sitePlan: any[]): SitePlanBuilding[] => {
  if (!sitePlan || !Array.isArray(sitePlan)) return [];

  return sitePlan.map((building: any) => ({
    building: building.building || "",
    number_of_floors: building.number_of_floors || 0,
    floor_details: [...(building.floor_details || [])]
      .map((detail: any) => ({
        floor: detail.floor || "",
        image: detail.url || detail.image || "",
      }))
      .sort((a, b) => parseFloat(a.floor) - parseFloat(b.floor)),
  }));
};

/**
 * Returns the id of the first room type that has at least one floor plan image.
 */
export const getDefaultRoomTypeId = (roomTypes: FloorPlanRoomType[]): string | null => {
  if (!roomTypes || !Array.isArray(roomTypes)) return null;

  const firstWithFloorPlan = roomTypes.find((rt) => rt.images.length > 0);
  return firstWithFloorPlan?.id || null;
};

/**
 * Generates alt text containing room type name and floor number.
 */
export const formatAltText = (roomTypeName: string, floor: string | null): string => {
  if (floor) {
    return `${roomTypeName} - Floor ${floor}`;
  }
  return roomTypeName;
};
